import { useAppDispatch } from '@/store';
import { setAiChatOpen } from '@/store/features/ui.feature';
import { INITIAL_FILTERS } from '@/types/search-filters';
import {
  ATTIO_SEARCH_PAGE_REFUSAL,
  crmExclusionsOnlyTheChatApplies,
  mapAgentSearchParamsToFilters,
} from '@/utils/agent-filters-to-search-filters';
import { encodeSearchQuery } from '@/utils/search-query-encoding';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {};
}

/** Why a saved audience can't open in Find People, in words, or null: an
 *  Attio exclusion has no section on the search page. */
export function audienceSearchRefusal(search: unknown): string | null {
  const people = record(record(search).people);
  return crmExclusionsOnlyTheChatApplies(people).some((clause) =>
    clause.startsWith('attio:')
  )
    ? `Can't open in Search: ${ATTIO_SEARCH_PAGE_REFUSAL}`
    : null;
}

/**
 * The filter blob Find People opens for a saved audience (`{people}` in the
 * people-search criteria Clu writes), or null when nothing maps. Same
 * translation as Clu's people-search handoff: unknown values are dropped by
 * the mapper, never passed through. An Attio exclusion has no section on the
 * search page, so an audience that carries one doesn't open there at all,
 * rather than open wider than the audience it names.
 */
export function audienceSearchQuery(search: unknown): string | null {
  if (audienceSearchRefusal(search)) return null;
  const people = { ...record(record(search).people) };
  // The API writes the criteria's own name; the mapper reads Clu's.
  if (people.function_categories_include && !people.function_categories) {
    people.function_categories = people.function_categories_include;
  }
  delete people.function_categories_include;
  const mapped = mapAgentSearchParamsToFilters(people);
  if (Object.keys(mapped).length === 0) return null;
  try {
    return encodeSearchQuery({
      tab: 'people',
      filters: { ...INITIAL_FILTERS, ...mapped },
    });
  } catch {
    return null;
  }
}

/** Opens a saved audience in Find People, with Clu docked beside it. */
export function useOpenAudienceInSearch() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  return useCallback(
    (q: string) => {
      void navigate({ to: '/finder', search: { tab: 'people', q } });
      dispatch(setAiChatOpen(true));
    },
    [navigate, dispatch]
  );
}
